/**
 * The tab's FTUX state, the Redux slice `ftux` (registered in src/store/index.ts): the truth for the tab, mirrored to
 * the owner's preferences by the transitions (the intro once per owner, a run per brand). Eager and import-light (the
 * store loads it for every owner): it imports only Redux Toolkit and types, never the layer or the pieces. FtuxRoot
 * publishes what eager code needs (the shipped pieces' meta, their availability, the gate) into it.
 *
 * Progress per brand: Lumio's hello and the menu's map present the studio once per owner (`intro`); the pieces run
 * per brand (`runs`, by brand id), so switching to brand B never carries brand A's place over. `progress` is the run
 * on screen: the intro while it is at the hello or the map, then the displayed brand's run (`brandId`), or null while
 * that brand has none yet. brandShown swaps it as the displayed brand changes.
 *
 * Two lifetimes:
 *  - the layer's: `mounted`, `shipped`, `probed`, `availability`, `gate`, published by FtuxRoot while it is mounted.
 *    mountedSet(false) on its unmount puts everything back (turning the `ftux` flag off mid-run leaves no pick card on Home
 *    and no paused nudges);
 *  - the owner's: the intro, the runs, the one on screen, its phase, view, skips and saver, which resetFtux puts back
 *    (the logout and identity-change listeners) while the mounted layer's own parts stay for the next owner.
 */
import { createSlice, type Draft, type PayloadAction } from '@reduxjs/toolkit';

import type { BrandPieceId, SkipReason } from '../catalog';
import type { FtuxTargetId } from '../targets';
import type { FtuxRecords } from './persistence';
import type {
  FtuxGate,
  FtuxGateReason,
  FtuxIntro,
  FtuxPatch,
  FtuxPhase,
  FtuxProgress,
  FtuxSave,
  FtuxView,
  PieceAvailability,
  PieceMeta,
  SkippedPiece,
} from './types';

export type FtuxState = {
  /** FtuxRoot is mounted (the `ftux` flag on, nobody signed in as the owner): every reader requires it. */
  mounted: boolean;
  /** The owner the run was read for (authentication.user.user_id), null until then. */
  userId: string | null;
  /** The runs were read from the owner's preferences (only once they loaded reliably for this owner). */
  hydrated: boolean;
  /** Which read of the owner's records this is (a new one on every read): a write's answer that lands after another
   *  read leaves it alone. 0 before any. */
  readId: number;
  /** A stored record is newer than this build knows: no start, no writes (gate reason newer-record). */
  newer: boolean;
  /** Lumio's hello and the menu's map, once per owner; null until the owner has started them. */
  intro: FtuxIntro | null;
  /** Each brand's run, by brand id: read from the owner's preferences, then as this tab moves them. */
  runs: Record<string, FtuxProgress>;
  /** The brand the run on screen belongs to (the displayed brand, once brandShown caught up with it). */
  brandId: string | null;
  /** The run on screen: the intro at the hello or the map, else the brand's run, or null when there is none yet. */
  progress: FtuxProgress | null;
  phase: FtuxPhase;
  /** The piece of the pop-up, in play, or acknowledged (during the acknowledgment the run already waits on the
   *  next one). */
  active: BrandPieceId | null;
  /** The view Lumio's card shows while a piece plays or acknowledges. */
  view: FtuxView | null;
  /** The pieces this build ships, in the script's order; null until FtuxRoot has published them. */
  shipped: PieceMeta[] | null;
  /** Each shipped piece's availability as its probe last reported it. */
  probed: Partial<Record<BrandPieceId, PieceAvailability>>;
  /**
   * The availability the run acts on (Spotlight's card, the transitions; none yet reads as checking): the probes'
   * reports, or none at all while the pieces wait for the brand's deployment (piecesWaitOf).
   */
  availability: Partial<Record<BrandPieceId, PieceAvailability>>;
  /** Why the flow passed pieces by in this run (the QA strip says it); not persisted. */
  skips: Partial<Record<BrandPieceId, SkipReason>>;
  /** The last thing of the studio a step looked for and did not find in this run (the QA strip says it); not
   *  persisted. */
  missed: FtuxTargetId | null;
  /** The start rule as the gate last published it; null until then. */
  gate: FtuxGate | null;
  save: FtuxSave;
};

type RunPart = Pick<FtuxState, 'phase' | 'active' | 'view' | 'skips' | 'missed'>;
type OwnerPart = RunPart &
  Pick<FtuxState, 'userId' | 'hydrated' | 'readId' | 'newer' | 'intro' | 'runs' | 'brandId' | 'progress' | 'save'>;

/** A run's moment-to-moment state at its start. */
const runStart = (): RunPart => ({ phase: 'none', active: null, view: null, skips: {}, missed: null });

/** The owner's part at its start: no run read, nothing in play, nothing being written. */
const ownerStart = (): OwnerPart => ({
  ...runStart(),
  userId: null,
  hydrated: false,
  readId: 0,
  newer: false,
  intro: null,
  runs: {},
  brandId: null,
  progress: null,
  save: { inFlight: false, pending: {}, error: null },
});

/**
 * The run on screen for `intro` and the brand's `run`: the intro while the owner is at the hello or the map, then the
 * brand's own run, or null (no intro yet, or a brand with no run yet: the start rule starts one). Pure.
 */
export const progressOf = (intro: FtuxIntro | null, run: FtuxProgress | undefined): FtuxProgress | null => {
  if (intro === null) return null;
  if (intro.status !== 'done') return { run: intro.run, status: intro.status, piece: null, outcomes: {}, deferred: [] };
  return run ?? null;
};

const initialState: FtuxState = {
  mounted: false,
  shipped: null,
  probed: {},
  availability: {},
  gate: null,
  ...ownerStart(),
};

/** Writes `part` into the draft, typed field by field. */
const put = <K extends keyof FtuxState>(state: Draft<FtuxState>, part: Pick<FtuxState, K>): void => {
  Object.assign(state, part);
};

/** The gate reasons that end a piece in play: the brand, or the record, changed under it. */
const LATCH_RELEASES: readonly FtuxGateReason[] = ['no-brand', 'brand-switching', 'newer-record'];

/** A piece plays or acknowledges: a transient close of the gate (a session or brands reload) does not stop it. */
const isPieceOn = (phase: FtuxPhase): boolean => phase === 'playing' || phase === 'ack';

/**
 * Whether the layer renders: mounted, the run read, and the gate open, or latched while a piece plays or
 * acknowledges, so a transient close never unmounts it mid-piece. The latch lets go on a brand switch, a brand gone
 * or a newer record (gateSet then ends the piece), on an identity change (resetFtux) and on impersonation (the layer
 * unmounts). Read it through selectFtuxCanRun, never gate.canRun.
 */
export const canRunOf = (ftux: FtuxState): boolean => {
  if (!ftux.mounted || !ftux.hydrated || ftux.gate === null) return false;
  if (ftux.gate.canRun) return true;
  return isPieceOn(ftux.phase) && (ftux.gate.reason === null || !LATCH_RELEASES.includes(ftux.gate.reason));
};

/**
 * Whether the pieces wait for the brand's deployment under `gate` (gate.deploy): no Spotlight card, no pop-up, no
 * start. Lumio's hello and the map go on, and so does a piece already in play.
 */
export const piecesWaitOf = (gate: FtuxGate | null): boolean => gate !== null && gate.deploy !== null;

/**
 * The pieces start (`wait`) or stop waiting for the deployment. While they wait, the run acts on the not-deployed
 * availability, none reported (every piece checking): no card shows, and the run waits on its next piece instead of
 * skipping or deferring one on what a deploying brand reports. An open pop-up closes, its piece waiting again. A piece
 * in play and its acknowledgment go on. Once deployed, the probes' reports come back, and FtuxRoot's reconcile moves
 * the run on from there.
 */
const holdPieces = (state: Draft<FtuxState>, wait: boolean): void => {
  if (!wait) {
    state.availability = { ...state.probed };
    return;
  }
  state.availability = {};
  if (state.phase === 'intro') put(state, { phase: 'none', active: null });
};

const sameGate = (a: FtuxGate | null, b: FtuxGate): boolean =>
  a !== null && a.canRun === b.canRun && a.canStart === b.canStart && a.reason === b.reason && a.deploy === b.deploy;

const sameView = (a: FtuxView | null, b: FtuxView | null): boolean =>
  a === b || (a !== null && b !== null && a.piece === b.piece && a.key === b.key);

/** The run on screen becomes `next`; another run (another `run`) starts with nothing in play. */
const showRun = (state: Draft<FtuxState>, next: FtuxProgress | null): void => {
  if (next === null || next.run !== state.progress?.run) put(state, runStart());
  state.progress = next;
};

const ftuxSlice = createSlice({
  name: 'ftux',
  initialState,
  reducers: {
    /** FtuxRoot mounted (true) or unmounted (false: everything back to the start, the owner's and the layer's). */
    mountedSet: (state, action: PayloadAction<boolean>) => {
      if (!action.payload) return initialState;
      state.mounted = true;
      return undefined;
    },
    /**
     * The owner's intro and brand runs as read from their preferences (readFtuxRecords), the displayed brand's on
     * screen (`brandId`, null while no brand is displayed yet), nothing in play.
     */
    hydrated: (
      state,
      action: PayloadAction<{ userId: string; brandId: string | null; records: FtuxRecords; readId: number }>,
    ) => {
      const { userId, brandId, records, readId } = action.payload;
      put(state, {
        ...ownerStart(),
        userId,
        hydrated: true,
        readId,
        newer: records.newer,
        intro: records.intro,
        runs: { ...records.runs },
        brandId,
        progress: brandId === null ? progressOf(records.intro, undefined) : progressOf(records.intro, records.runs[brandId]),
      });
    },
    /**
     * The displayed brand changed (at the bottom of a switch's fade, the gate still closed): its own run comes on
     * screen, or none yet. The intro and every brand's run stay as they are, and so does a write under way.
     */
    brandShown: (state, action: PayloadAction<string>) => {
      if (!state.hydrated || state.brandId === action.payload) return;
      state.brandId = action.payload;
      showRun(state, progressOf(state.intro, state.runs[action.payload]));
    },
    /**
     * The run on screen moved: at the hello or the map it is the owner's intro; past them it is the displayed brand's
     * run (the intro done). A new run (another `run`) starts with nothing in play.
     */
    progressSet: (state, action: PayloadAction<FtuxProgress>) => {
      const next = action.payload;
      if (next.status === 'hello' || next.status === 'lap') {
        state.intro = { run: next.run, status: next.status };
      } else {
        if (state.intro?.status !== 'done') state.intro = { run: state.intro?.run ?? next.run, status: 'done' };
        if (state.brandId !== null) state.runs[state.brandId] = next;
      }
      showRun(state, next);
    },
    /** The displayed brand's run is dropped (a QA jump to the hello or the map): it starts again after the map. */
    runDropped: (state) => {
      if (state.brandId !== null) delete state.runs[state.brandId];
    },
    /** Every brand's run is dropped (the QA strip's Restart): each starts again from its first piece. */
    runsCleared: (state) => {
      state.runs = {};
    },
    /** What the tab shows of the piece (none, its pop-up, in play, acknowledged), and which piece. */
    phaseSet: (state, action: PayloadAction<{ phase: FtuxPhase; active: BrandPieceId | null }>) => {
      state.phase = action.payload.phase;
      state.active = action.payload.active;
      if (!isPieceOn(state.phase)) state.view = null;
    },
    /** The view Lumio's card shows, published by the runner; ignored when no piece plays or acknowledges. */
    viewSet: (state, action: PayloadAction<FtuxView | null>) => {
      if (isPieceOn(state.phase) && !sameView(state.view, action.payload)) state.view = action.payload;
    },
    shippedSet: (state, action: PayloadAction<readonly PieceMeta[]>) => {
      state.shipped = [...action.payload];
    },
    /** A probe's report: kept, and acted on unless the pieces wait for the deployment. */
    availabilitySet: (state, action: PayloadAction<{ id: BrandPieceId; availability: PieceAvailability }>) => {
      const { id, availability } = action.payload;
      if (state.probed[id] !== availability) state.probed[id] = availability;
      if (!piecesWaitOf(state.gate) && state.availability[id] !== availability) state.availability[id] = availability;
    },
    /** Why pieces were passed by, for the QA strip. */
    skipsNoted: (state, action: PayloadAction<readonly SkippedPiece[]>) => {
      action.payload.forEach(({ id, reason }) => {
        state.skips[id] = reason;
      });
    },
    /** Something a step looked for was not on the page, for the QA strip. */
    missedNoted: (state, action: PayloadAction<FtuxTargetId>) => {
      state.missed = action.payload;
    },
    /**
     * The gate's verdict. A brand switch, a brand gone or a newer record ends what is open: the piece waits again.
     * The pieces start or stop waiting for the deployment as gate.deploy comes or goes (holdPieces).
     */
    gateSet: (state, action: PayloadAction<FtuxGate>) => {
      const gate = action.payload;
      if (sameGate(state.gate, gate)) return;
      const waited = piecesWaitOf(state.gate);
      state.gate = gate;
      if (!gate.canRun && gate.reason !== null && LATCH_RELEASES.includes(gate.reason) && state.phase !== 'none') {
        put(state, { phase: 'none', active: null, view: null });
      }
      if (piecesWaitOf(gate) !== waited) holdPieces(state, !waited);
    },
    /** Keys to write with the next POST, each as its transition left it (a later value for a key replaces it). */
    savePending: (state, action: PayloadAction<FtuxPatch>) => {
      state.save.pending = { ...state.save.pending, ...action.payload };
    },
    /** A POST takes every pending key. */
    saveStarted: (state) => {
      state.save.inFlight = true;
      state.save.pending = {};
    },
    saveDone: (state) => {
      state.save.inFlight = false;
      state.save.error = null;
    },
    /** The POST failed: its keys wait again, under any newer value, for the next write. */
    saveFailed: (state, action: PayloadAction<{ error: string; patch: FtuxPatch }>) => {
      state.save.inFlight = false;
      state.save.error = action.payload.error;
      state.save.pending = { ...action.payload.patch, ...state.save.pending };
    },
    /** The owner's part back to its start (logout, identity change); what the mounted layer published stays. */
    resetFtux: (state) => {
      put(state, ownerStart());
    },
  },
});

export const {
  mountedSet,
  hydrated,
  brandShown,
  progressSet,
  runDropped,
  runsCleared,
  phaseSet,
  viewSet,
  shippedSet,
  availabilitySet,
  skipsNoted,
  missedNoted,
  gateSet,
  savePending,
  saveStarted,
  saveDone,
  saveFailed,
  resetFtux,
} = ftuxSlice.actions;

export default ftuxSlice.reducer;
